import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { createPortal } from "react-dom";
import { Overlay, ModalPanel, BodyModal, FooterModal, ContentModal, ContentInner } from "./Modal.style";
export default function Modal({ open = true, onClose, children, buttons, containerComponent: Container, containerClassName, fullWidth = false, buttonsRow = false, disableAutoFocus = false, }) {
    const [portalTarget, setPortalTarget] = React.useState(null);
    const panelRef = React.useRef(null);
    const [shouldRender, setShouldRender] = React.useState(open);
    const [visualOpen, setVisualOpen] = React.useState(false);
    const raf1 = React.useRef(null);
    const raf2 = React.useRef(null);
    const focusTimeoutRef = React.useRef(null);
    React.useEffect(() => {
        if (open) {
            setShouldRender(true);
            setVisualOpen(false);
            raf1.current = requestAnimationFrame(() => {
                raf2.current = requestAnimationFrame(() => setVisualOpen(true));
            });
            return () => {
                if (raf1.current)
                    cancelAnimationFrame(raf1.current);
                if (raf2.current)
                    cancelAnimationFrame(raf2.current);
            };
        }
        else {
            setVisualOpen(false);
        }
    }, [open]);
    const handleTransitionEnd = (e) => {
        if (!open && e.propertyName === "transform") {
            setShouldRender(false);
        }
    };
    React.useEffect(() => {
        if (!shouldRender)
            return;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [shouldRender, onClose]);
    React.useEffect(() => {
        setPortalTarget(typeof document !== "undefined" ? document.body : null);
    }, []);
    React.useEffect(() => {
        if (!shouldRender)
            return;
        const body = document.body;
        const html = document.documentElement;
        const scrollY = window.scrollY || window.pageYOffset;
        const prevBodyOverflow = body.style.overflow;
        const prevBodyPosition = body.style.position;
        const prevBodyTop = body.style.top;
        const prevBodyWidth = body.style.width;
        const prevHtmlOverflow = html.style.overflow;
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        html.style.overflow = 'hidden';
        const prevActive = document.activeElement;
        if (!disableAutoFocus) {
            focusTimeoutRef.current = setTimeout(() => {
                var _a;
                (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
        return () => {
            var _a;
            body.style.overflow = prevBodyOverflow;
            body.style.position = prevBodyPosition;
            body.style.top = prevBodyTop;
            body.style.width = prevBodyWidth;
            html.style.overflow = prevHtmlOverflow;
            if (focusTimeoutRef.current !== null) {
                clearTimeout(focusTimeoutRef.current);
            }
            window.scrollTo(0, scrollY);
            (_a = prevActive === null || prevActive === void 0 ? void 0 : prevActive.focus) === null || _a === void 0 ? void 0 : _a.call(prevActive);
        };
    }, [shouldRender, disableAutoFocus]);
    if (!portalTarget || !shouldRender)
        return null;
    const modalContent = (_jsx(Overlay, { "data-open": visualOpen, role: "presentation", className: "modal-overlay", children: _jsx(ModalPanel, { ref: panelRef, role: "dialog", "aria-modal": "true", tabIndex: -1, "data-open": visualOpen, "data-full-width": fullWidth, onTransitionEnd: handleTransitionEnd, children: _jsxs(BodyModal, { className: "modal-body", children: [_jsx(ContentModal, { className: "modal-content", children: _jsx(ContentInner, { children: children }) }), buttons ? _jsx(FooterModal, { className: "modal-footer", "data-buttons-row": buttonsRow, children: buttons }) : null] }) }) }));
    const wrappedContent = Container ? (_jsx(Container, { className: containerClassName, children: modalContent })) : modalContent;
    return createPortal(wrappedContent, portalTarget);
}
//# sourceMappingURL=Modal.js.map